import React, { useMemo, useEffect, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import debounce from "lodash.debounce";
import { X } from "react-feather";
import { useMerchant } from "context/merchant";
import { useModalManager } from "shared/hooks/useModalManager";
import AddProductModal from "./AddProductModal";

export default function Toolbar({
  search,
  setSearch,

  // ✅ parent
  selectedCategory,
  handleCategoryChange,

  // ✅ sub
  selectedSubCategory,
  handleSubCategoryChange,

  // null = бүгд · "true" = зөвхөн харагдах · "false" = зөвхөн нуугдсан
  visibility,
  handleVisibilityChange,

  categories,          // [{value,label}] parent options (Бүх ангилал + parents)
  rawCategories,       // tree [{id,name,children:[]}]
  loadingCategories,
  categoriesError,
  onRefetch,
}) {
  const [searchLoading, setSearchLoading] = useState(false);
  // ⚠️ Талбарын утгыг ДОТООД state-д барина. Өмнө нь `value={search}` байсан
  //    бөгөөд `handleSearchChange` нь debounce-ийн ДАРАА `setSearch(value)`-ийг
  //    бас ШУУД дууддаг байсан тул debounce огт ажиллахгүй, үсэг бүр дээр
  //    хүсэлт явдаг байв (уралдаан → буруу жагсаалт үлддэг).
  const [inputValue, setInputValue] = useState(search || "");
  const { role } = useMerchant();
  const { closeModal, currentModal, openModal } = useModalManager();

  const debouncedSetSearch = useMemo(() => {
    return debounce((value) => {
      setSearch(value);
      setSearchLoading(false);
    }, 500);
  }, [setSearch]);

  useEffect(() => {
    return () => debouncedSetSearch.cancel();
  }, [debouncedSetSearch]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setSearchLoading(true);
    debouncedSetSearch(value);
  };

  const clearSearch = () => {
    debouncedSetSearch.cancel();
    setInputValue("");
    setSearchLoading(false);
    setSearch("");
  };

  const onAdd = () => openModal("add-product");

  // ✅ сонгосон parent-ийн children-үүд
  const subCategoryOptions = useMemo(() => {
    if (!selectedCategory) return [];
    const parent = rawCategories?.find(
      (p) => String(p.id) === String(selectedCategory)
    );
    const children = parent?.children || [];
    return children.map((c) => ({ value: String(c.id), label: c.name }));
  }, [rawCategories, selectedCategory]);

  const showSub = selectedCategory && subCategoryOptions.length > 0;

  return (
    <div className="page-title-box">
      <div className="page-title-right d-flex align-items-center mb-2 gap-2">
        {/* SEARCH */}
        <div style={{ position: "relative", width: "200px" }}>
          <Form.Control
            type="text"
            placeholder="Хайх..."
            value={inputValue}
            onChange={handleSearchChange}
            className="bg-white"
            style={{ paddingRight: "30px" }}
          />
          {(searchLoading || inputValue) && (
            <div
              style={{
                position: "absolute",
                right: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                cursor: searchLoading ? "default" : "pointer",
                color: "#999",
              }}
              onClick={!searchLoading ? clearSearch : undefined}
            >
              {searchLoading ? (
                <Spinner
                  animation="border"
                  size="sm"
                  style={{
                    width: "16px",
                    height: "16px",
                    color: "#999",
                    borderWidth: "1px",
                  }}
                />
              ) : (
                <X size={16} />
              )}
            </div>
          )}
        </div>

        {/* PARENT CATEGORY */}
        <Form.Select
          value={selectedCategory || ""}
          onChange={(e) => {
            const value = e.target.value === "" ? null : e.target.value;
            handleCategoryChange(value);
            // ✅ parent солигдвол sub reset
            handleSubCategoryChange(null);
          }}
          className="me-2"
          style={{ width: "200px" }}
          disabled={loadingCategories || !!categoriesError}
        >
          {loadingCategories && <option>Ангилал татаж байна...</option>}

          {!loadingCategories &&
            !categoriesError &&
            categories.map((cat) => (
              <option key={cat.value ?? "all"} value={cat.value || ""}>
                {cat.label}
              </option>
            ))}

          {categoriesError && <option disabled>{categoriesError}</option>}
        </Form.Select>

        {/* SUB CATEGORY */}
        <Form.Select
          value={selectedSubCategory || ""}
          onChange={(e) => {
            const value = e.target.value === "" ? null : e.target.value;
            handleSubCategoryChange(value);
          }}
          className="me-2"
          style={{ width: "200px" }}
          disabled={!showSub || loadingCategories || !!categoriesError}
        >
          <option value="">
            {showSub ? "Дэд ангилал (бүгд)" : "Дэд ангилал байхгүй"}
          </option>
          {showSub &&
            subCategoryOptions.map((sc) => (
              <option key={sc.value} value={sc.value}>
                {sc.label}
              </option>
            ))}
        </Form.Select>

        {/* ХАРАГДАЦ — дууссан/нууссан барааг шууд олох зам. Backend-ийн
            `isVisible` параметр аль хэдийн байсан, зөвхөн энэ хяналт дутуу байв. */}
        <Form.Select
          value={visibility ?? ""}
          onChange={(e) =>
            handleVisibilityChange(e.target.value === "" ? null : e.target.value)
          }
          className="me-2"
          style={{ width: "170px" }}
        >
          <option value="">Бүх бараа</option>
          <option value="true">Зөвхөн идэвхтэй</option>
          <option value="false">Зөвхөн хаасан</option>
        </Form.Select>

        <Button variant="primary" className="border" onClick={onRefetch}>
          <i className="mdi mdi-autorenew" />
        </Button>

        {role === "PRODUCT_MANAGER" || role === "ADMIN" ? (
          <Button variant="primary" className="border" onClick={onAdd}>
            <i className="mdi mdi-plus me-1"></i>
            Нэмэх
          </Button>
        ) : null}
      </div>

      <AddProductModal
        show={currentModal === "add-product"}
        handleClose={closeModal}
      />
    </div>
  );
}
